import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Clock, User, Calendar, ArrowRight } from "lucide-react";
import { useArticles } from "../hooks/useArticles";
import { useLatestArticles } from '../hooks/useArticles';
import { formatDateShort } from "../utils/formatters";
import ImageCarousel from "./ImageCarousel";

import axios from "axios";

const backendUrl = "https://justurbane.in/backend_php/public";

const HomePage = () => {
  const { data: articles = [], isLoading, error } = useArticles({ limit: 20 });
  const { data: latestArticles = [], isLoading: isLatestLoading, error: latestError } = useLatestArticles({ limit: 5 });
  const [sliceLength, setSliceLength] = useState(300);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          `${backendUrl}/api/admin/categories/list`
        );
        if (Array.isArray(response.data)) {
          setCategories(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch categories", error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const updateSliceLength = () => {
      const width = window.innerWidth;
      if (width >= 1600) setSliceLength(55);
      else if (width >= 1280) setSliceLength(45);
      else if (width >= 1024) setSliceLength(35);
      else if (width >= 768) setSliceLength(20);
      else setSliceLength(7);
    };
    updateSliceLength();
    window.addEventListener("resize", updateSliceLength);
    return () => window.removeEventListener("resize", updateSliceLength);
  }, []);

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        Failed to load articles: {error.message}
      </div>
    );

  if (isLoading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 mx-auto mb-3"></div>
          <p className="text-gray-600 text-sm">Loading premium content...</p>
        </div>
      </div>
    );

  const getArticleRoute = (article) => `/article/${article.slug}`;

  // Category filters
  const fashionArticles = articles.filter((a) => a.category === "fashion");
  const technologyArticles = articles.filter(
    (a) => a.category === "technology"
  );
  const travelArticles = articles.filter((a) => a.category === "travel");
  const luxuryArticles = articles.filter((a) => a.category === "luxury");
  const foodArticles = articles.filter((a) => a.category === "food");
  const peopleArticles = articles.filter((a) => a.category === "people");

  const heroArticle =
    luxuryArticles.find((a) => a.slug?.includes("sunseeker")) ||
    luxuryArticles[0] ||
    articles[1];

  const mixedArticles = [
    ...fashionArticles.slice(0, 2),
    ...technologyArticles.slice(0, 2),
    ...travelArticles.slice(0, 2),
  ];

  const featuredArticles = articles.slice(0, 3); // show 3 spotlight articles

  const featured_Articles = articles.slice(0, 9); // show 3 spotlight articles
  const firstActiveCategory = categories.filter((category) => 
      category.category_status === "ACTIVE" && 
      category.dashboard_visibility_status === "ACTIVE"
  )[0];

  const firstCategoryName = firstActiveCategory ? firstActiveCategory.name : "fashion";

  return (
    <div
      className="min-h-screen bg-gray-50 px-4 md:px-8 py-6"
      style={{
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8 flex flex-col gap-10">
            <div className="bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100">
              <ImageCarousel />
            </div>

            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                  Spotlight
                </h2>
                {/* to={`/category/${category.name}`} */}
                
                <Link
                  to={`/category/${firstCategoryName}`} 
                  className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                >
                  View all &rarr;
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {featuredArticles.map((article, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ y: -4 }}
                    className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300"
                  >
                    <Link
                      to={getArticleRoute(article)}
                      className="block h-full flex flex-col"
                    >
                      <div className="relative overflow-hidden h-48">
                        {article.hero_image ? (
                          <img
                            src={article.hero_image}
                            alt={article.title}
                            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-200" />
                        )}
                        <div className="absolute top-3 left-3">
                          <span className="bg-white/90 backdrop-blur-sm px-2 py-1 rounded-md text-xs font-bold text-gray-900 shadow-sm">
                            Feature
                          </span>
                        </div>
                      </div>

                      <div className="p-5 flex flex-col flex-1">
                        <h3 className="text-lg font-bold text-gray-900 leading-snug group-hover:text-blue-600 transition-colors line-clamp-2 mb-2">
                          {article.title}
                        </h3>

                        <div className="mt-auto flex items-center text-xs text-gray-500 font-medium">
                          <span className="flex items-center truncate">
                            <User className="w-3.5 h-3.5 mr-1.5" />
                            {article.author || "Editorial"}
                          </span>
                          <span className="mx-2">•</span>
                          <span className="flex items-center min-w-fit">
                            <Clock className="w-3.5 h-3.5 mr-1.5" />
                            {formatDateShort(article.created_at)}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-4 space-y-8 sticky top-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-5 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900">
                  Latest Updates
                </h2>
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
              </div>

              <div className="divide-y divide-gray-100">
                {latestArticles.map((article, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ x: 4 }}
                    className="group bg-white hover:bg-gray-50 transition-colors duration-200"
                  >
                    <Link
                      to={getArticleRoute(article)}
                      className="flex items-start gap-4 p-5"
                    >
                      <div className="flex-shrink-0 relative overflow-hidden  w-24 h-24 border border-gray-100">
                        {article.hero_image ? (
                          <img
                            src={article.hero_image}
                            alt={article.title}
                            className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400">
                            <span className="text-xs">No Img</span>
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1 block">
                          {article.category || "News"}
                        </span>
                        <h3 className="text-sm font-bold text-gray-900 leading-snug line-clamp-2 group-hover:text-blue-700 transition-colors mb-1.5">
                          {article.title}
                        </h3>
                        {article.summary && (
                          <p className="text-xs text-gray-500 line-clamp-1 mb-2">
                            {article.summary}
                          </p>
                        )}
                        <div className="flex items-center text-xs text-gray-400">
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatDateShort(article.created_at)}
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {categories
        .filter((category) => category.category_status === "ACTIVE")
        .filter((category) => category.dashboard_visibility_status === "ACTIVE")
        .map((category, index) => {
          const categoryArticles = articles.filter(
            (a) => a.category?.toLowerCase() === category.name?.toLowerCase()
          );

          if (categoryArticles.length === 0) return null;

          return (
            <section
              key={category.id}
              className={`w-full py-12 border-t border-gray-100 ${
                index % 2 === 0 ? "bg-white" : "bg-gray-50"
              }`}
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                    {category.display_name} 
                  </h2>
                  <Link
                    to={`/category/${category.name}`}
                    className="group flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-gray-500 hover:text-blue-600 transition-colors"
                  >
                    View All
                    <ArrowRight className="w-3 h-3 transform group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                  {categoryArticles.slice(0, 10).map((article, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.05 }}
                      whileHover={{ y: -5 }}
                      className="group bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300"
                    >
                      <Link
                        to={getArticleRoute(article)}
                        className="flex flex-col h-full"
                      >
                        <div className="aspect-[4/3] overflow-hidden relative">
                          <img
                            src={
                              article.hero_image ||
                              "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 320 180'><rect width='320' height='180' fill='%23f3f4f6'/><path d='M140 60h40v40h-40z' fill='%239ca3af'/><path d='M120 120h80v10h-80z' fill='%239ca3af'/></svg>"
                            }
                            alt={article.title}
                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                            onError={(e) => {
                              e.currentTarget.src =
                                "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 320 180'><rect width='320' height='180' fill='%23f3f4f6'/><path d='M140 60h40v40h-40z' fill='%239ca3af'/><path d='M120 120h80v10h-80z' fill='%239ca3af'/></svg>";
                            }}
                          />
                          <div className="absolute top-2 left-2">
                            <span className="bg-white/90 backdrop-blur-sm px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider text-gray-900 shadow-sm">
                              {category.display_name}
                            </span>
                          </div>
                        </div>

                        <div className="p-4 flex flex-col flex-1">
                          <h3 className="text-sm font-bold text-gray-900 leading-snug line-clamp-2 group-hover:text-blue-600 transition-colors mb-3">
                            {article.title}
                          </h3>

                          <div className="mt-auto flex items-center gap-2 text-[11px] text-gray-400 font-medium">
                            <Clock className="w-3 h-3" />
                            <span>{formatDateShort(article.created_at)}</span>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            </section>
          );
        })}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {heroArticle && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex flex-col h-full"
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="w-1.5 h-6 bg-red-600 rounded-full"></span>
                <h2 className="text-xl font-bold text-gray-900 tracking-tight uppercase">
                  Cover Story
                </h2>
              </div>

              <Link
                to={`/article/${heroArticle.slug}`}
                className="group relative w-full rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className="aspect-video w-full overflow-hidden">
                  <img
                    src={heroArticle.hero_image}
                    alt={heroArticle.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>

                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent opacity-90" />

                <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 flex flex-col justify-end">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-bold bg-blue-600 text-white uppercase tracking-wide mb-3 w-fit">
                    {heroArticle.category}
                  </span>

                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight mb-3">
                    {heroArticle.title}
                  </h2>

                  <p className="text-gray-200 text-sm sm:text-base line-clamp-2 mb-4 max-w-xl">
                    {heroArticle.summary}
                  </p>

                  <div className="flex items-center gap-4 text-xs font-medium text-gray-300 border-t border-gray-700 pt-4 w-full">
                    <div className="flex items-center gap-1.5">
                      <User className="w-3.5 h-3.5 text-white" />
                      <span className="text-white">
                        {heroArticle.author_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      <span>{heroArticle.reading_time || "5 min read"}</span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          )}

          {travelArticles.length > 0 && (
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-6 bg-green-600 rounded-full"></span>
                  <h2 className="text-xl font-bold text-gray-900 tracking-tight uppercase">
                    Travel &amp; Leisure
                  </h2>
                </div>
                <Link
                  to="/category/travel"
                  className="flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-green-600 uppercase tracking-wider transition-colors"
                >
                  View All <ArrowRight className="w-3 h-3" />
                </Link>
              </div>

              <div className="flex flex-col gap-6">
                {travelArticles[0] && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                  >
                    <Link
                      to={`/article/${travelArticles[0].slug}`}
                      className="group block relative rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300"
                    >
                      <div className="aspect-video w-full overflow-hidden">
                        <img
                          src={travelArticles[0].hero_image}
                          alt={travelArticles[0].title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                      <div className="absolute bottom-0 p-5 w-full">
                        <h4 className="text-lg sm:text-xl font-bold text-white leading-snug mb-2 group-hover:text-green-300 transition-colors">
                          {travelArticles[0].title}
                        </h4>
                        <div className="flex items-center gap-3 text-xs text-gray-300 font-medium">
                          <span>
                            {formatDateShort(travelArticles[0].created_at)}
                          </span>
                          <span className="w-1 h-1 rounded-full bg-gray-500"></span>
                          <span>
                            {travelArticles[0].reading_time || "4 min"}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                )}

                <div className="flex flex-col gap-4">
                  {travelArticles.slice(1, 4).map((article, index) => (
                    <motion.div
                      key={article.id}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Link
                        to={`/article/${article.slug}`}
                        className="group flex gap-4 items-start p-3 bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all duration-300"
                      >
                        <div className="relative w-32 aspect-video flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                          <img
                            src={article.hero_image}
                            alt={article.title}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                          />
                        </div>
                        <div className="flex flex-col py-1 min-w-0">
                          <h5 className="text-sm font-bold text-gray-900 leading-snug line-clamp-2 mb-2 group-hover:text-green-700 transition-colors">
                            {article.title}
                          </h5>
                          <p className="text-xs text-gray-500 line-clamp-1 mb-2">
                            {article.summary}
                          </p>
                          <div className="flex items-center text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                            <span>{formatDateShort(article.created_at)}</span>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

     

      {/* FEATURED ARTICLES SECTION - LIGHT THEME REDESIGN */}
      {featured_Articles && featured_Articles.length > 0 && (
        <section className="bg-slate-50 py-16 border-y border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* HEADER - CLEAN & MINIMAL */}
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
              <div>
                <h2 className="text-4xl font-serif font-bold text-gray-900 tracking-tight">
                  Featured Stories
                </h2>
                <div className="h-1 w-20 bg-blue-600 mt-3 rounded-full"></div>
              </div>
              <p className="text-gray-500 max-w-md font-medium">
                Curated exclusive content and premium deep-dives from our
                editorial team.
              </p>
            </div>

            <div className="grid lg:grid-cols-12 gap-8">
              {/* LEFT COLUMN: PRIMARY FOCUS (8/12) */}
              <div className="lg:col-span-8 space-y-8">
                {/* MAIN HERO CARD */}
                {featured_Articles[0] && (
                  <Link
                    to={getArticleRoute(featured_Articles[0])}
                    className="group relative block bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 transition-all duration-500 hover:shadow-xl hover:-translate-y-1"
                  >
                    <div className="aspect-[16/9] sm:aspect-[21/9] lg:aspect-[16/8] overflow-hidden">
                      <img
                        src={featured_Articles[0].hero_image}
                        alt={featured_Articles[0].title}
                        className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>

                    {/* CONTENT BOX - LIGHT OVERLAY */}
                    <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 bg-gradient-to-t from-black/80 via-black/40 to-transparent text-white lg:from-white lg:via-white/95 lg:to-transparent lg:text-gray-900 lg:relative lg:bg-none">
                      <div className="space-y-3">
                        <span className="inline-block px-3 py-1 bg-blue-600 text-white text-[10px] font-bold uppercase tracking-widest rounded lg:bg-blue-50 lg:text-blue-700">
                          {featured_Articles[0].category}
                        </span>
                        <h3 className="text-2xl sm:text-3xl font-bold leading-tight group-hover:text-blue-600 transition-colors">
                          {featured_Articles[0].title}
                        </h3>
                        <p className="text-gray-100 lg:text-gray-600 text-sm sm:text-base line-clamp-2 leading-relaxed">
                          {featured_Articles[0].summary}
                        </p>
                        <div className="pt-2 flex items-center text-xs font-bold uppercase tracking-wider text-white lg:text-blue-600">
                          Read Story{" "}
                          <span className="ml-2 group-hover:translate-x-2 transition-transform">
                            →
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                )}

                {/* TWO SUB-FEATURES */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {featured_Articles.slice(1, 3).map((article) => (
                    <Link
                      key={article.id}
                      to={getArticleRoute(article)}
                      className="group bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-lg transition-all"
                    >
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={article.hero_image}
                          alt={article.title}
                          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <h4 className="font-bold text-gray-900 text-lg leading-snug group-hover:text-blue-600 transition-colors line-clamp-2">
                          {article.title}
                        </h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* RIGHT COLUMN: LIST SIDEBAR (4/12) */}
              <div className="lg:col-span-4">
                <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm sticky top-24">
                  <h3 className="text-xs font-bold text-blue-600 uppercase tracking-[0.2em] mb-6 flex items-center">
                    Latest Highlights
                    <span className="ml-3 h-px flex-1 bg-gray-100"></span>
                  </h3>

                  <div className="space-y-8">
                    {featured_Articles.slice(3, 7).map((article) => (
                      <Link
                        key={article.id}
                        to={getArticleRoute(article)}
                        className="group flex gap-4 items-start"
                      >
                        <div className="w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-lg overflow-hidden border border-gray-100">
                          <img
                            src={article.hero_image}
                            alt={article.title}
                            className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                        <div className="flex flex-col pt-1">
                          <h4 className="text-sm sm:text-base font-bold text-gray-900 line-clamp-2 leading-tight group-hover:text-blue-600 transition-colors">
                            {article.title}
                          </h4>
                          <p className="mt-2 text-xs text-gray-500 line-clamp-2 leading-relaxed">
                            {article.summary}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* NEWSLETTER SECTION */}
      <section className="bg-white py-8 border-t border-gray-200">
        <div className="w-full px-4 sm:px-4 lg:px-12 text-center">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4">
            Stay Updated with Just Urbane
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Get the latest in luxury lifestyle, fashion, and culture delivered
            to your inbox
          </p>
          <div className="flex max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email address"
              // className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              className="w-58 sm:w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg sm:rounded-l-lg 
                   focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm sm:text-base"
            />
            <button className="bg-gray-900 text-white px-4 py-3 rounded-r-lg hover:bg-gray-800 transition-colors font-semibold">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
